import * as React from "react";
import {
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  TextField,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { CmpDatePickerField001V1 } from "../../Component/Common/ToolBox/ToolBoxV1";
import { DecimalEditCell, IntegerEditCell } from "./Components/CustomEditCells";
import { confirmAlert } from "react-confirm-alert";
import { fAlertToast, fKeyTabEnterInput } from "../../Utility/Utilitys";
import { CmpSelectField } from "../../Component/Common/ToolBox/ToolBox";

const nf2 = (v) =>
  v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2);

const makeColumns = (formState) => {
  const exRate = Number(formState?.ExchangeRate ?? 1);

  return [
    {
      field: "CategoryName",
      headerName: "Category",
      minWidth: 180,
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "B_PriceRange",
      headerName: "Range",
      width: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "B_ReqOty",
      headerName: "ReqQty",
      type: "number",
      width: 70,
      editable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "B_PurQty",
      headerName: "PurQty",
      type: "number",
      width: 70,
      align: "center",
      headerAlign: "center",
      editable: false,
    },
    {
      field: "B_BalanceQty",
      headerName: "BalQty",
      type: "number",
      width: 70,
      align: "center",
      headerAlign: "center",
      editable: false,
    },
    // Editable: Qty
    {
      field: "B_Qty",
      headerName: "Qty",
      type: "number",
      width: 60,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderEditCell: (params) => <IntegerEditCell {...params} />,
      cellClassName: "editable-cell", // ✅ add class
    },
    {
      field: "uom",
      headerName: "UOM",
      type: "number",
      width: 60,
      align: "center",
      headerAlign: "center",
      editable: false,
    },
    // Editable: Pack Qty
    {
      field: "B_PackQty",
      headerName: "PackQty",
      // type: "number",
      width: 80,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderEditCell: (params) => <IntegerEditCell {...params} />,
      cellClassName: "editable-cell", // ✅ add class
    },
    // Computed: Base Qty = B_Qty * B_PackQty
    {
      field: "B_BaseQty",
      headerName: "BaseQty",
      type: "number",
      width: 80,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (params) => {
        const qty = Number(params.row.B_Qty ?? 0);
        const pack = Number(params.row.B_PackQty ?? 0);
        const v = qty * pack;
        return Number.isFinite(v) ? v : null;
      },
      sortable: false,
      filterable: false,
    },
    // Editable: Cost (FC)
    {
      field: "expectedCost",
      headerName: "Expt.Cost",
      type: "number",
      width: 95,
      align: "center",
      headerAlign: "center",
      editable: false,
      hide: true,
    },
    {
      field: "B_ExpCost",
      headerName: "Exp.Cost",
      type: "number",
      width: 95,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (params) => {
        const expCost = Number(params.row.expectedCost ?? 0);
        const e = expCost / exRate;
        return Number.isFinite(e) ? e : null;
      },
    },

    {
      field: "B_CostFc",
      headerName: "Cost (FC)",
      type: "number",
      width: 95,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderEditCell: (params) => <DecimalEditCell {...params} />,
      valueFormatter: (p) => nf2(p.value),
      cellClassName: "editable-cell", // ✅ add class
    },

    // Computed: Cost (LC) = Cost(FC) * ExchangeRate
    {
      field: "B_CostLc",
      headerName: "Cost (LC)",
      type: "number",
      width: 95,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (params) => {
        const fc = Number(params.row.B_CostFc ?? 0);
        const v = fc * exRate;
        return Number.isFinite(v) ? v : null;
      },
      valueFormatter: (p) => nf2(p.value),
      sortable: false,
      filterable: false,
    },
    {
      field: "B_NetAmountFC",
      headerName: "Net.Amt(FC)",
      type: "number",
      width: 90,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (params) => {
        const qty = Number(params.row.B_Qty ?? 0);
        const costFc = Number(params.row.B_CostFc ?? 0);
        const v = qty * costFc;
        return Number.isFinite(v) ? v : null;
      },
      valueFormatter: (p) => nf2(p.value),
      sortable: false,
      filterable: false,
    },
    {
      field: "B_NetAmountLC",
      headerName: "Net.Amt(LC)",
      type: "number",
      width: 90,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueGetter: (params) => {
        const qty = Number(params.row.B_Qty ?? 0);
        const costLc = Number(params.row.B_CostLc ?? 0);
        const v = qty * costLc;
        return Number.isFinite(v) ? v : null;
      },
      valueFormatter: (p) => nf2(p.value),
      sortable: false,
      filterable: false,
    },
    {
      field: "MainGroupName",
      headerName: "Main Group",
      minWidth: 120,
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "SubGroupName",
      headerName: "Sub Group",
      minWidth: 120,
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
  ];
};

export function PurchaseDetailModal({
  open,
  onClose,
  formState,
  errors,
  setErrors,
  setField,
  Currency,
  RefTransId,
  handleSubmit,
  onLoad,
  header,
  isMobile,
  isActionBtnDisabled,
  purchaseDetilSummaryRows,
  setPurchaseDetilSummaryRows,
  filteredPayload,
}) {
  const apiRef = useGridApiRef();
  const columns = React.useMemo(
    () => makeColumns(formState),
    [formState?.ExchangeRate]
  );

  const processRowUpdate = (newRow, oldRow) => {
    const clean = (v) => (v === "" ? null : v);

    const qty = Number(newRow.B_Qty ?? 0);
    const pack = Number(newRow.B_PackQty ?? 0);
    const fc = Number(newRow.B_CostFc ?? 0);
    const ex = Number(formState?.ExchangeRate ?? 1);

    const baseQty = qty * pack;
    const costLc = fc * ex;
    const netAmountFC = qty * fc;
    const netAmountLC = qty * costLc;

    const updated = {
      ...oldRow,
      ...newRow,
      B_PackQty: clean(newRow.B_PackQty),
      B_Qty: clean(newRow.B_Qty),
      B_CostFc: clean(newRow.B_CostFc),

      // recomputed derived fields
      B_BaseQty: Number.isFinite(baseQty) ? baseQty : null,
      B_CostLc: Number.isFinite(costLc) ? costLc : null,
      B_NetAmountFC: Number.isFinite(netAmountFC) ? netAmountFC : null,
      B_NetAmountLC: Number.isFinite(netAmountLC) ? netAmountLC : null,
    };

    setPurchaseDetilSummaryRows((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    );

    return updated;
  };

  const totals = React.useMemo(() => {
    const ex = Number(formState?.ExchangeRate ?? 1);
    const rows = purchaseDetilSummaryRows ?? [];
    return rows.reduce(
      (acc, r) => {
        const qty = Number(r?.B_Qty ?? 0);
        const costFc = Number(r?.B_CostFc ?? 0);
        const exptCost = Number(r?.expectedCost ?? 0);
        const costLc = costFc * ex; // compute like your column's valueGetter

        acc.costFc += costFc; // sum of unit Cost(FC) if you want it
        acc.costLc += costLc; // sum of unit Cost(LC) as displayed
        acc.netFc += qty * costFc; // Net(FC) = Qty * Cost(FC)
        acc.netLc += qty * costLc; // Net(LC) = Qty * Cost(LC)
        acc.B_ExpCost += exptCost / ex;
        return acc;
      },
      { costFc: 0, costLc: 0, netFc: 0, netLc: 0, B_ExpCost: 0 }
    );
  }, [purchaseDetilSummaryRows, formState?.ExchangeRate]);

  const handleExport = () => {
    apiRef.current.exportDataAsCsv(); // or exportDataAsExcel(), etc.
  };
  const handlePrint = () => {
    apiRef.current.exportDataAsPrint();
  };

  const handleEnterFocus = (e, nextId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = document.getElementById(nextId);
      if (next) next.focus();
    }
  };

  // Sync when exchange rate changes
  React.useEffect(() => {
    const ex = Number(formState?.ExchangeRate ?? 1);
    if (!purchaseDetilSummaryRows?.length) return;

    setPurchaseDetilSummaryRows((prev) =>
      prev.map((r) => {
        const qty = Number(r?.B_Qty ?? 0);
        const fc = Number(r?.B_CostFc ?? 0);
        const expectedCost = Number(r?.expectedCost ?? 0);
        const costLc = fc * ex;
        const expectCost = expectedCost / ex;

        return {
          ...r,
          B_CostLc: Number.isFinite(costLc) ? costLc : null,
          B_NetAmountFC: Number.isFinite(qty * fc) ? qty * fc : null,
          B_NetAmountLC: Number.isFinite(qty * costLc) ? qty * costLc : null,
          B_ExpCost: Number.isFinite(expectCost) ? expectCost : null,
        };
      })
    );
  }, [formState?.ExchangeRate]);

  React.useEffect(() => {
    if (open) onLoad();
  }, [open]);

  return (
    <Dialog fullScreen={isMobile} maxWidth="xl" open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#0d73bb",
          padding: "6px 0px 6px 12px !important",
          color: "#fff",
        }}
      >
        Purchase – Detail
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ height: "75vh", padding: { xs: "12px 6px", md: "16px 20px" } }}
      >
        <Grid container spacing={{ xs: 1, md: 2 }}>
          <Grid item xs={5} sm={2} md={1.2}>
            <TextField
              size="small"
              fullWidth
              label="Trans ID"
              value={formState.TransId}
              InputLabelProps={{
                shrink: true,
              }}
              disabled
              InputProps={{ readOnly: true, tabIndex: -1 }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000", // text color when disabled
                  color: "#000", // fallback
                  backgroundColor: "lightgrey",
                },
              }}
            />
          </Grid>

          <Grid item xs={7} sm={2.5} md={1.8}>
            <CmpDatePickerField001V1
              xLabelText="Trans Date"
              xName="TransDate"
              xValue={formState.TransDate}
              xOnChange={(newValue) => {
                setField("TransDate", newValue?.$d);
                setTimeout(() => {
                  document.getElementById("Currency").focus();
                }, 200);
              }}
              xError={!!errors.TransDate}
              xErrorMessage={errors.TransDate}
              xtabIndex={-1}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                select
                size="small"
                fullWidth
                label="Currency"
                id="Currency"
                value={formState.Currency}
                onChange={(e) => {
                  setField("Currency", e?.target?.value);
                  setTimeout(() => {
                    document.getElementById("RefNo").focus();
                  }, 200);
                }}
                error={!!errors.Currency}
                helperText={errors.Currency}
                disabled={header.Pur_Status === "POST"}
              >
                {Currency?.map((c) => (
                  <MenuItem key={c.UID} value={c.UID}>
                    {c.Name1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3.5} md={2}>
            <TextField
              size="small"
              fullWidth
              label="Exchange Rate"
              value={formState.ExchangeRate}
              disabled
              error={!!errors.ExchangeRate}
              helperText={errors.ExchangeRate}
              InputProps={{ readOnly: true, tabIndex: -1 }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000", // text color when disabled
                  color: "#000", // fallback
                  backgroundColor: "lightgrey",
                },
              }}
            />
          </Grid>

          <Grid item xs={4} sm={2.5} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Order ID"
              value={RefTransId}
              InputLabelProps={{
                shrink: true,
              }}
              disabled
              InputProps={{ readOnly: true, tabIndex: -1 }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000", // text color when disabled
                  color: "#000", // fallback
                  backgroundColor: "lightgrey",
                },
              }}
            />
          </Grid>

          <Grid item xs={3.5} sm={2.5} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Ref No"
              id="RefNo"
              value={formState.RefNo}
              onChange={(e) => {
                const value = e.target.value;
                setField("RefNo", value);
                setErrors((prev) => ({
                  ...prev,
                  RefNo: "",
                }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setTimeout(() => {
                    document.getElementsByName("RefDate")[0]?.focus();
                  }, 200);
                }
              }}
              error={!!errors.RefNo}
              helperText={errors.RefNo}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={4.5} sm={2.5} md={2}>
            <CmpDatePickerField001V1
              xLabelText="Ref Date"
              xName="RefDate"
              xValue={formState.RefDate}
              xOnChange={(newValue) => {
                setField("RefDate", newValue?.$d);
                 setTimeout(() => {
                    document.getElementById("Vendor").focus();
                  }, 200);
              }}
              xError={!!errors.RefDate}
              xErrorMessage={errors.RefDate}
              xtabIndex={-1}
            />
          </Grid>

          <Grid item xs={12} sm={4.5} md={6}>
            <TextField
              size="small"
              fullWidth
              label="Vendor"
              id="Vendor"
              value={formState.Vendor}
              onChange={(e) => {
                const value = e.target.value;
                setField("Vendor", value);
                setErrors((prev) => ({
                  ...prev,
                  Vendor: "",
                }));
              }}
                onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setTimeout(() => {
                    document.getElementById("Narration").focus();
                  }, 200);
                }
              }}
              error={!!errors.Vendor}
              helperText={errors.Vendor}
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              label="Narration"
              id="Narration"
              value={formState.Narration}
              onChange={(e) => setField("Narration", e?.target?.value)}
              autoComplete="off"
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ height: { xs: "40vh", sm: "44vh", md: "45vh" } }}>
          <Button
            size="small"
            variant="contained"
            sx={{ mr: 2, mb: 1 }}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ mr: 2, mb: 1 }}
            onClick={handlePrint}
          >
            Print
          </Button>
          <DataGridPro
            apiRef={apiRef}
            rowHeight={30}
            headerHeight={40}
            rows={purchaseDetilSummaryRows ?? []}
            columns={columns}
            editMode="cell"
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(err) => console.error(err)}
            experimentalFeatures={{ newEditingApi: true }} // ✅ REQUIRED for v5
            getRowId={(row) => row?.id}
            getRowClassName={(params) =>
              Number(params.row.B_Qty) > 0 ? "row-highlight-blue" : ""
            }
            hideFooterPagination
            components={{
              Footer: () => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    p: 1,
                    borderTop: "1px solid #ccc",
                    backgroundColor: "#f9f9f9",
                    fontWeight: "bold",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: "80px !important",
                      fontSize: "10px !important",
                      mx: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    Exp.Cost: {totals.B_ExpCost.toFixed(2)}
                  </Box>
                  <Box
                    sx={{
                      minWidth: "80px !important",
                      fontSize: "10px !important",
                      mx: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    Cost (FC): {totals.costFc.toFixed(2)}
                  </Box>
                  <Box
                    sx={{
                      minWidth: "80px !important",
                      fontSize: "10px !important",
                      mx: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    Cost (LC): {totals.costLc.toFixed(2)}
                  </Box>
                  <Box
                    sx={{
                      minWidth: "80px !important",
                      fontSize: "10px !important",
                      mx: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    Net (FC): {totals.netFc.toFixed(2)}
                  </Box>
                  <Box
                    sx={{
                      minWidth: "80px !important",
                      fontSize: "10px !important",
                      mx: { xs: 0.5, sm: 1, md: 1.5 },
                    }}
                  >
                    Net (LC): {totals.netLc.toFixed(2)}
                  </Box>
                </Box>
              ),
            }}
            sx={{
              border: 1,
              borderColor: "primary.light",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#0d73bb",
                color: "white",
              },
              // highlight editable cells (view mode)
              "& .editable-cell": {
                backgroundColor: "#fff3e0",
                border: " 1px solid lightgray",
              },
              // highlight input while editing
              "& .MuiDataGrid-cell--editing": {
                backgroundColor: "#fffde7",
              },
              "& .MuiDataGrid-cell--editing .MuiInputBase-root": {
                backgroundColor: "#fffef5",
                border: "1px solid #f57c00",
                boxShadow: "0 0 0 1px #f57c00 inset",
                borderRadius: 1,
              },
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-pinnedColumnHeaders":
                {
                  backgroundColor: "#0d73bb",
                  color: "#fff",
                },
              "& .MuiDataGrid-pinnedColumnHeaders": {
                zIndex: 2,
                textAlign: "left",
              },
              "& .MuiDataGrid-pinnedColumns--left": {
                boxShadow: "4px 0 6px -4px rgba(37, 15, 15, 0.2)",
              },
            }}
            initialState={{
              pinnedColumns: !isMobile ? { left: ["CategoryName"] } : {},
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          size="small"
          // disabled={isActionBtnDisabled}
          disabled={
            isActionBtnDisabled ||
            String(header?.Close_Status).toUpperCase() !== "ENTER"
          }
          onClick={() => handleSubmit(false)}
        >
          Save
        </Button>
        <Button
          variant="contained"
          size="small"
          //  disabled={isActionBtnDisabled}
          disabled={
            isActionBtnDisabled ||
            String(header?.Close_Status).toUpperCase() !== "ENTER"
          }
          color="success"
          onClick={() => {
            if (filteredPayload?.length == 0) {
              fAlertToast("FAILED", "Detail should not be Empty");
              return;
            }

            confirmAlert({
              title: "Confirm Action",
              message: "Are you sure do you want to post?",
              buttons: [
                {
                  className: "alertBtn",
                  label: "Yes",
                  onClick: () => {
                    handleSubmit(true);
                  },
                },
                {
                  className: "alertBtn",
                  label: "No",
                  // onClick: () => "",
                },
              ],
            });
          }}
        >
          Post
        </Button>
      </DialogActions>
    </Dialog>
  );
}
